import { useCallback, useEffect, useState } from "react";
import { GraduationCap, Loader2, Plus, X, Trash2, Users, BookOpen, Target, Sparkles } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import AudiencePicker from "../../components/os/AudiencePicker";
import { api } from "../../lib/api";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ld-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function LeadershipDev() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);

  const load = useCallback(() => { api.get("/leadership/programs").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/leadership/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_leader: false, areas: [] })); load(); }, [load]);

  const openNew = () => {
    setF({ title: "", description: "", objetivos: "", duration_weeks: 8, start_date: "",
      aud: { audience: "users", user_ids: [], areas: [] }, resources: [{ title: "", url: "" }],
      create_course: false, course_topics: "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Escribe el título del programa");
    setBusy(true);
    try {
      await api.post("/leadership/programs", {
        title: f.title, description: f.description, objetivos: f.objetivos,
        audience: f.aud.audience, participant_ids: f.aud.user_ids, areas: f.aud.areas,
        duration_weeks: Number(f.duration_weeks) || 8, start_date: f.start_date || null,
        resources: (f.resources || []).filter((r) => r.title || r.url),
        create_course: f.create_course, course_topics: f.course_topics.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Programa de liderazgo creado" + (f.create_course ? " · curso generándose en Academy" : ""));
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { try { await api.delete(`/leadership/programs/${id}`); load(); } catch { toast.error("Error"); } };

  const setRes = (i, k, val) => { const r = [...f.resources]; r[i] = { ...r[i], [k]: val }; setF({ ...f, resources: r }); };

  return (
    <OSLayout title="OMNIA Leadership Development">
      <div className="max-w-4xl space-y-6" data-testid="leadership-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-navy/55 max-w-xl">Crea programas de desarrollo de liderazgo. Tú eres el <b className="text-navy">mentor</b>. Elige participantes desde el directorio (uno, varios, un área o toda la empresa) y genera contenido con OMNIA Academy.</p>
          {meta?.is_leader && <button onClick={openNew} className="btn btn-primary gap-2" data-testid="ld-new"><Plus className="w-4 h-4" /> Nuevo programa</button>}
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-navy/10 rounded-2xl p-10 text-center text-navy/40" data-testid="ld-empty">
            <GraduationCap className="w-8 h-8 mx-auto mb-2 text-navy/25" /> Aún no hay programas de liderazgo.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={`ld-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-base font-medium text-navy">{p.title}</div>
                  {meta?.is_leader && <button onClick={() => del(p.id)} className="text-navy/30 hover:text-destructive" data-testid={`ld-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
                {p.description && <p className="text-sm text-navy/55 mt-1">{p.description}</p>}
                <div className="flex flex-wrap gap-3 mt-3 text-xs text-navy/50">
                  <span className="inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Mentor: {p.mentor_name}</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(p.participant_ids || []).length} participantes</span>
                  <span className="inline-flex items-center gap-1"><Target className="w-3.5 h-3.5" /> {p.duration_weeks} semanas</span>
                  {p.course_id && <span className="inline-flex items-center gap-1 text-violet"><BookOpen className="w-3.5 h-3.5" /> Curso en Academy</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && f && (
        <Modal title="Nuevo programa de liderazgo" onClose={() => setOpen(false)}>
          <div className="space-y-4">
            <input className="field" placeholder="Título del programa" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ld-title" />
            <textarea className="field" rows={2} placeholder="Descripción" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ld-desc" />
            <textarea className="field" rows={2} placeholder="Objetivos de desarrollo" value={f.objetivos} onChange={(e) => setF({ ...f, objetivos: e.target.value })} data-testid="ld-obj" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-navy/60 mb-1 block">Duración (semanas)</label><input type="number" className="field" value={f.duration_weeks} onChange={(e) => setF({ ...f, duration_weeks: e.target.value })} data-testid="ld-weeks" /></div>
              <div><label className="text-xs text-navy/60 mb-1 block">Inicio</label><input type="date" className="field" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="ld-start" /></div>
            </div>

            <div>
              <label className="text-xs font-medium text-navy/70 mb-2 block">Participantes</label>
              <AudiencePicker value={f.aud} onChange={(aud) => setF({ ...f, aud })} areas={meta?.areas || []} testid="ld-aud" />
            </div>

            <div>
              <label className="text-xs font-medium text-navy/70 mb-2 block">Recursos (opcional)</label>
              {f.resources.map((r, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                  <input className="field" placeholder="Título del recurso" value={r.title} onChange={(e) => setRes(i, "title", e.target.value)} />
                  <input className="field" placeholder="URL" value={r.url} onChange={(e) => setRes(i, "url", e.target.value)} />
                </div>
              ))}
              <button type="button" onClick={() => setF({ ...f, resources: [...f.resources, { title: "", url: "" }] })} className="text-xs text-violet hover:underline">+ Añadir recurso</button>
            </div>

            <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
              <input type="checkbox" checked={f.create_course} onChange={(e) => setF({ ...f, create_course: e.target.checked })} data-testid="ld-create-course" />
              Generar un curso en OMNIA Academy para este programa (contenido con IA)
            </label>
            {f.create_course && <input className="field" placeholder="Temas del curso, separados por coma" value={f.course_topics} onChange={(e) => setF({ ...f, course_topics: e.target.value })} data-testid="ld-course-topics" />}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button>
              <button onClick={submit} disabled={busy} className="btn btn-primary gap-2" data-testid="ld-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear programa</button>
            </div>
          </div>
        </Modal>
      )}
    </OSLayout>
  );
}
